import * as Sentry from '@sentry/react';

/**
 * Browser-side version of the old `app/utils/handleResponse.ts` (which imported
 * Remix server modules). Same `{ data, error }` contract so ported model
 * functions behave identically.
 *
 * On 401 we hand off to `onUnauthorized` (wired by AuthBridge to Auth0 logout/
 * login) instead of the old server `redirect('/logout')`.
 */
let onUnauthorized: () => void = () => {
    window.location.assign('/login');
};

export function setUnauthorizedHandler(fn: () => void): void {
    onUnauthorized = fn;
}

export type ApiResult<T> =
    | { data: T; error: null }
    | { data: null; error: string }
    | { data: null; error: null }; // 204 No Content

export async function handleResponse<T = unknown>(
    response: Response,
    verbAndUrl?: string
): Promise<ApiResult<T>> {
    if (response.status === 401) {
        onUnauthorized();
        return { data: null, error: 'Unauthorized' };
    }

    if (response.status === 404) {
        return { data: null, error: 'Resource not found' };
    }

    if (response.status === 204) {
        return { data: null, error: null };
    }

    const resClone1 = response.clone();
    const resClone2 = response.clone();

    if (!response.ok) {
        let parsedError: { message?: string; error?: string } | undefined;
        let bodyText: string | undefined;
        try {
            parsedError = await resClone1.json().catch(() => undefined);
        } catch {
            // ignore
        }
        if (!parsedError) {
            bodyText = await resClone2.text().catch(() => undefined);
        }

        const errorMessage = parsedError?.message ?? parsedError?.error ?? undefined;

        if (response.status >= 500) {
            Sentry.withScope((scope) => {
                scope.setLevel('error');
                scope.setTag('http.status', String(response.status));
                if (verbAndUrl) scope.setTag('request.verbAndUrl', verbAndUrl);
                scope.setContext('response', {
                    url: response.url,
                    status: response.status,
                    statusText: response.statusText,
                    body: errorMessage ?? bodyText ?? '[no-body]',
                });
                Sentry.captureException(
                    new Error(
                        errorMessage || `HTTP ${response.status} ${response.statusText}`
                    )
                );
            });
        }

        return { data: null, error: errorMessage ?? 'Failed to fetch' };
    }

    try {
        // Some endpoints (e.g. POST .../trigger-analysis) return 200 with an
        // empty body — treat those like 204 rather than failing JSON.parse.
        const text = await response.text();
        if (!text) return { data: null, error: null };
        const data = JSON.parse(text) as T;
        return { data, error: null };
    } catch (error) {
        console.error(`ERROR parsing ${response.url}:`, error);
        return { data: null, error: 'Failed to fetch' };
    }
}
